import type { Node } from "prosemirror-model";
import { Comment } from "../../Editor/plugins/commentPlugin";
import { CommitJSON } from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import type { SavedPaymentMethod } from "../../shared/types/SubscriptionTypes";

export type ValueOf<T> = T[keyof T];

/* keep in sync with server prosemirror-server/src/helpers/clientActions.ts */
export const PM_ACTIONS = {
    NEW_DOC: 'nd',
    ARCHIVE_DOC: 'ar',
    TRASH_DOC: 'tr',
    DELETE_FOREVER: 'df',
    AUTH: 'auth',
    CLOSE_DOC: 'cd',

    ACTIVITY_SYNC: 'as',
    ACTIVITY_PARTIAL: 'ap',

    SNAPSHOT_LIST: 'sl',
    SNAPSHOT_GET: 'sg',
    SNAPSHOT_RESTORE: 'sre',
    SNAPSHOT_FORCE_SAVE: 'sfs',
    SNAPSHOT_SAVE_AS: 'ssave',

    AI_QUIZ_CREATE: 'ai_quiz_create',
    AI_QUIZ_USAGE: 'ai_quiz_usage',

    PAYMENT_GATEWAY: 'payment_gateway',
    PAYMENT_INVOICE: 'payment_invoice',
    PAYMENT_INVOICE_UPGRADE: 'payment_invoice_upgrade',
    PAYMENT_INVOICE_DOWNLOAD: 'payment_invoice_download',
    PAYMENT_SUBSCRIPTION_PREVIEW: 'payment_subscription_preview',
    PAYMENT_SUBSCRIPTION_UPDATE: 'payment_subscription_update',
    PAYMENT_METHOD_DETAILS: 'payment_method_details',
    PAYMENT_METHOD_UPDATE_TRANSACTION: 'payment_method_update_transaction',
    PAYMENT_METHOD_DELETE: 'payment_method_delete',

    COLLAB_SYNC: 's',
    COLLAB_GET_DOCUMENT: 'gd',
    COLLAB_GET_EVENTS: 'ge',
    COLLAB_SEND_EVENTS: 'se',
    COLLAB_SYNC_SELECTION: 'ss',

    WORKSPACE_DEL: 'ws_del',
    LISTS_GET: 'lists_get',
    LOCATION_COUNT: 'location_count',
} as const;

export type PM_ACTION =
    | 'nd'
    | 'ar'
    | 'tr'
    | 'df'
    | 'auth'
    | 's'
    | 'gd'
    | 'ge'
    | 'se'
    | 'payment_gateway'
    | 'invoice'
    | 'payment_subscription_preview'
    | 'payment_subscription_update'
    | 'payment_method_details'
    | 'payment_method_update_transaction'
    | 'payment_method_delete'
    | 'lists_get'
    | 'location_count';
// Note: keep PM_ACTION union in sync when adding new actions.

export interface ServerResponse {
    a: PM_ACTION;
    s?: number; // http status
    error?: string;
}

export interface ServerResponseAuth extends ServerResponse {
    exp: number;
}

export interface GetCollabDocResponse extends ServerResponse {
    docId: string;
    doc: Node;
    s: number;
    version: number;
    latestEditor: string;
    updatedAt: string;
    comments: Comment[];
    commentVersion: number
}

export interface CollabEventsResponse extends ServerResponse {
    serverVersion: number;
    commits: CommitJSON[];
    comments: Object;
}

export interface SnapshotsListResponse extends ServerResponse {
    items: any[];
    nextToken: string | null;
}

export interface AIUsageResponse extends ServerResponse {
    usageCount: number;
}

export interface AIQuizCreateResponse extends ServerResponse {
    quizId: string;
    docId: string;
}

export interface PaymentGatewayResponse extends ServerResponse {
    gateway: 'paddle' | 'invoice';
}

export interface PaymentInvoiceDownloadResponse extends ServerResponse {
    s3path?: string;
}

export interface SubscriptionPreviewTotals {
    subtotal: number;
    tax: number;
    total: number;
    currency_code: string;
}

export interface PaymentSubscriptionPreviewResponse extends ServerResponse {
    requestHash: string;
    immediateTotals?: SubscriptionPreviewTotals;
    nextTotals?: SubscriptionPreviewTotals;
}

export interface PaymentSubscriptionUpdateResponse extends ServerResponse {
    success: boolean;
    subscriptionId?: string;
}

export interface PaymentMethodDetailsResponse extends ServerResponse {
    methods: SavedPaymentMethod[];
    paymentProvider?: string;
}

export interface PaymentMethodUpdateTransactionResponse extends ServerResponse {
    transactionId: string;
    checkoutUrl?: string | null;
    status: string;
    subscriptionId?: string;
}

export interface PaymentMethodDeleteResponse extends ServerResponse {
    success: boolean;
    paymentMethodId?: string;
}

export interface LocationCountResponse extends ServerResponse {
    count: number;
}

export type ServerResponseTypes = {
    nd: ServerResponse,
    ar: ServerResponse,
    tr: ServerResponse,
    df: ServerResponse,
    auth: ServerResponseAuth,

    sl: SnapshotsListResponse,
    sg: ServerResponse,
    sre: ServerResponse,
    sfs: ServerResponse,
    ssave: ServerResponse,

    ai_quiz_create: AIQuizCreateResponse,
    ai_quiz_usage: AIUsageResponse,

    s: ServerResponse,
    gd: GetCollabDocResponse,
    ge: CollabEventsResponse,
    se: ServerResponse,
    ss: ServerResponse,
    ws_del: ServerResponse,
    payment_gateway: PaymentGatewayResponse,
    payment_invoice: ServerResponse,
    payment_invoice_upgrade: ServerResponse,
    payment_invoice_download: PaymentInvoiceDownloadResponse,
    payment_subscription_preview: PaymentSubscriptionPreviewResponse,
    payment_subscription_update: PaymentSubscriptionUpdateResponse,
    payment_method_details: PaymentMethodDetailsResponse,
    payment_method_update_transaction: PaymentMethodUpdateTransactionResponse,
    payment_method_delete: PaymentMethodDeleteResponse,
    lists_get: { items: any[] } & ServerResponse,
    location_count: LocationCountResponse,

    searchDocuments: ServerResponse,
};


export interface RequestState {
    promise: Promise<any>,
    getUniqueRequestHash: (msg: any) => string,
    resolve: (msg: any) => void,
    reject: (reason: any) => void,
    timeout: ReturnType<typeof setTimeout>,
    abortController?: AbortController,
}
